import React, {useState} from "react";
import {Alert, Box, Button, Chip, Link, Stack, Typography} from "@mui/material";
import {
    Add as AddIcon,
    DeleteOutline as DeleteIcon,
    EditOutlined as EditIcon,
    LockOutlined as LockIcon,
    SpeedOutlined as GaugeIcon
} from "@mui/icons-material";
import {Link as RouterLink} from "react-router-dom";
import {formatReais} from "../../../shared/utils/formatters";
import {
    FONTES,
    listaComApontamento,
    listaSemApontamento,
    rotulo,
    unidadeDeCobranca,
    versaoPublicada,
    versoesNoSistema
} from "../../Metricas/metricasRegras";
import {completude, plural} from "../sistemaPagina";
import ApontarMetricaDialog from "./ApontarMetricaDialog";
import ConfirmacaoDialog from "./ConfirmacaoDialog";
import SecaoCard from "./SecaoCard";
import {CompletudeChip} from "./SistemaVisual";

const nomeDaVersao = (versao) => `${versao.nome_plano} v${versao.numero}`;

const LinhaDoApontamento = ({apontamento, metrica, sistemaId, onEditar, onRemover}) => {
    const versoes = versoesNoSistema(metrica, sistemaId);
    const custo = Number(apontamento.custo_referencia);
    return (
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{py: 2, borderTop: 1, borderColor: 'divider'}}>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography variant="body2" component="div">
                    <Link component={RouterLink} to={`/metricas/${apontamento.metrica_id}`} underline="hover" fontWeight={700}>{metrica?.nome || apontamento.nome}</Link>
                    {' '}<Box component="span" sx={{fontFamily: 'monospace', color: 'text.secondary', fontSize: 12}}>{metrica?.chave || apontamento.chave}</Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">{apontamento.como_mede}</Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{mt: 1}}>
                    <Chip size="small" color="primary" variant="outlined" label={`Fonte: ${rotulo(FONTES, apontamento.fonte)}`} />
                    {apontamento.bloqueia
                        ? <Chip size="small" color="success" variant="outlined" icon={<LockIcon fontSize="small" />} label="Consegue bloquear" />
                        : <Chip size="small" variant="outlined" label="Não bloqueia" />}
                    {custo > 0 && metrica ? <Chip size="small" color="warning" variant="outlined" label={`Custo ${formatReais(custo)} por ${unidadeDeCobranca(metrica)}`} /> : null}
                    <Chip size="small" variant="outlined" label={versoes.length ? `Em ${plural(versoes.length, 'versão de plano', 'versões de plano')}` : 'Ainda sem plano'} />
                </Stack>
            </Box>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{flexShrink: 0}}>
                <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />} onClick={onEditar}>Editar</Button>
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={onRemover}>Remover</Button>
            </Stack>
        </Stack>
    );
};

/*
 * Métricas apontadas no sistema (BBF-167 a, protótipo v2 "Métricas" da página do sistema). Só as apontadas aparecem
 * nos limites e valores dos planos do sistema. A lista vai inteira no PUT do sistema (BBF-149); métrica numa versão
 * publicada de plano do sistema não sai, e dos rascunhos o limite sai junto.
 */
export default function MetricasSecao({sistema, pendencias, catalogo, erroDoCatalogo, onSalvar, onRecusa}) {
    const [dialogo, setDialogo] = useState(null);
    const [remover, setRemover] = useState(null);
    const apontamentos = sistema.metricas || [];
    const metricaDe = (id) => (catalogo || []).find((item) => item.id === id);
    const apontadas = new Set(apontamentos.map((item) => item.metrica_id));
    const livres = (catalogo || []).filter((metrica) => !apontadas.has(metrica.id));
    const vendavel = sistema.status !== 'INTERNO';

    const pedirRemocao = (apontamento) => {
        const metrica = metricaDe(apontamento.metrica_id);
        const versoes = versoesNoSistema(metrica, sistema.id);
        const publicadas = versoes.filter(versaoPublicada);
        if (publicadas.length) {
            onRecusa(`Não dá para remover: ${metrica.nome} está em ${publicadas.map(nomeDaVersao).join(', ')}. Versão publicada não muda.`);
            return;
        }
        setRemover({apontamento, metrica, rascunhos: versoes.filter((versao) => !versaoPublicada(versao))});
    };

    const salvarApontamento = async (form, metrica) => {
        const editando = apontadas.has(form.metrica_id);
        await onSalvar(listaComApontamento(apontamentos, form), `métrica ${metrica.nome}`, editando
            ? `Métrica ${metrica.nome} atualizada. Vale para as próximas faturas.`
            : `Métrica ${metrica.nome} apontada. Ela já aparece nos limites dos planos do sistema e pode ser definida nos rascunhos.`);
        setDialogo(null);
    };

    return (
        <SecaoCard
            id="metricas"
            titulo="Métricas"
            subtitulo="Só as métricas apontadas aparecem nos limites e valores dos planos deste sistema."
            chip={<CompletudeChip completude={completude('metricas', {sistema, pendencias})} />}
            acoes={(
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogo({editando: null})}
                    disabled={!catalogo || !livres.length}
                    title={catalogo && !livres.length ? 'Todas as métricas do cadastro já foram apontadas' : undefined}
                >
                    Apontar métrica
                </Button>
            )}
        >
            {erroDoCatalogo ? <Alert severity="warning" variant="outlined" sx={{mb: 2}}>Não deu para carregar o cadastro de métricas: {erroDoCatalogo}</Alert> : null}
            {!vendavel ? (
                <Alert severity="info" variant="outlined" sx={{mb: 2}}>
                    <strong>Uso interno.</strong> Este sistema não entra em planos. Apontar métricas aqui serve só para medir.
                </Alert>
            ) : null}

            {apontamentos.length ? (
                <Box sx={{mx: {xs: -2, md: -3}, px: {xs: 2, md: 3}}}>
                    {apontamentos.map((apontamento) => (
                        <LinhaDoApontamento
                            key={apontamento.metrica_id}
                            apontamento={apontamento}
                            metrica={metricaDe(apontamento.metrica_id)}
                            sistemaId={sistema.id}
                            onEditar={() => setDialogo({editando: apontamento})}
                            onRemover={() => pedirRemocao(apontamento)}
                        />
                    ))}
                </Box>
            ) : (
                <Stack alignItems="center" spacing={1} sx={{py: 4, color: 'text.secondary', textAlign: 'center'}}>
                    <GaugeIcon />
                    <Typography variant="body2">
                        Nenhuma métrica apontada.{vendavel ? ' Sem métrica, o sistema não vai à venda: o plano não teria limite de usuários nem o que cobrar além da mensalidade.' : ''}
                    </Typography>
                </Stack>
            )}

            {catalogo && livres.length ? (
                <Typography variant="body2" color="text.secondary" sx={{pt: 2, borderTop: 1, borderColor: 'divider'}}>
                    Ainda não apontadas: {livres.map((metrica, i) => (
                        <React.Fragment key={metrica.id}>
                            {i ? ', ' : ''}<Link component={RouterLink} to={`/metricas/${metrica.id}`}>{metrica.nome}</Link>
                        </React.Fragment>
                    ))}. Falta alguma? <Link component={RouterLink} to="/metricas/nova">Cadastre uma métrica</Link>.
                </Typography>
            ) : null}

            <ApontarMetricaDialog
                aberto={Boolean(dialogo)}
                sistema={sistema}
                catalogo={catalogo || []}
                editando={dialogo?.editando || null}
                onSalvar={salvarApontamento}
                onFechar={() => setDialogo(null)}
            />

            <ConfirmacaoDialog
                aberto={Boolean(remover)}
                titulo={`Remover ${remover?.metrica?.nome || 'a métrica'} do sistema ${sistema.nome}?`}
                botao="Remover"
                perigo
                onConfirmar={async () => {
                    const {apontamento, metrica} = remover;
                    await onSalvar(listaSemApontamento(apontamentos, apontamento.metrica_id), `métrica ${metrica?.nome || ''} removida`, `Métrica ${metrica?.nome || ''} removida do sistema ${sistema.nome}.`);
                    setRemover(null);
                }}
                onFechar={() => setRemover(null)}
            >
                <Typography variant="body2">
                    A métrica sai dos limites e valores dos planos deste sistema.
                    {remover?.rascunhos?.length ? ` Os rascunhos ${remover.rascunhos.map(nomeDaVersao).join(', ')} perdem o que estava definido para ela.` : ''}
                </Typography>
            </ConfirmacaoDialog>
        </SecaoCard>
    );
}
